/// <reference path="../../../../node_modules/@ms/office-online-strings/en-US/transcription-strings.d.ts" />

import * as React from 'react';
import { AppAudioPlayer, AppButton, AppTranscriptCard, IAudioPlayer, ITranscriptData, AppTranscriptCardChangeParameter, AppAudioPlayerFeatureName, getSecondaryButtonOverrideStyles } from '../../components';
import { getTranscribingPageStyles, getStartOverButtonCustomStyles, getAddToDocumentButtonCustomStyles, getExplainedLabelStyles, getButtonContainerStackTokens } from './Transcribing.styles';
import { RouteComponentProps } from 'react-router-dom';
import { Stack, Label, Dialog,  DialogFooter } from 'office-ui-fabric-react';
import { SetEditingMode, SetHighlightedIndex, StartOverClicked, TranscriptionDataModified, InitializeTranscriptionData, AddTranscriptionToDocument, AddTranscriptionSegmentToDocument, SetAudioPlaying, SetShowStartOverConfirmDialog } from '../../actions/TranscribingActions';
import transcribingStore from '../../stores/TranscribingStore';
import { INDEX_NONE } from '../../stores/TranscribingStore';
import { ChangeEvent } from '../../stores/utils';
import { LogEvents } from '../../../utils/LogEvents';
import { TranscriptKeys } from './telemetry/TranscriptKeys';
import { StartOverKeys } from '../telemetry/StartOverKeys';
import { DurationMeasureType } from '../../../backend/interfaces/ILogEvent';
import { RenderToPaneKeys } from '../telemetry/RenderToPaneKeys';
import { ITranscriptionManager } from '../../../backend/interfaces/ITranscriptionManager';
import { AppExplainedLabel } from '../../components/AppExplainedLabel/AppExplainedLabel';
import { ITranscriptionSourceFile } from '../../../backend/interfaces/ITranscriptionSourceFileProvider';
import { getDialogContentProps, getModalProps, getTopLabelStyles } from '../../components/Popups/Popup.styles';
import { AppLayoutSettings } from '../common.styles';

export interface TranscribingState {
  /**
   * Track the index of the card which is currently being edited
   */
  editingIndex: number;

  /**
   * Track the index of the card which is currently highlighted
   */
  highlightedIndex: number;

  /**
   * Source audio file
   */
  sourceFile: ITranscriptionSourceFile | null;

  /**
   * Data for the Transcript cards
   */
  transcriptionData: ITranscriptData[];

  /**
   * Flag to check if audio is playing
   */
  isAudioPlaying: boolean;

  /**
   * Manager for the transcription
   */
  transcriptionManager: ITranscriptionManager;

  /**
   * Shows a modal dialog warning to the user before starting a new transcript
   */
  showStartOverConfirmDialog: boolean
}

export class Transcribing extends React.PureComponent<RouteComponentProps, TranscribingState> {
  private audioPlayerRef = React.createRef<IAudioPlayer>();
  private _isMounted: boolean;

  constructor(props: RouteComponentProps) {
    super(props);
    this._isMounted = false;

    this.state = transcribingStore.getState();
    transcribingStore.on(ChangeEvent.Change, this._onStoreChanged);
  }

  public componentDidMount() {
    this._isMounted = true;
    LogEvents.Transcript.Start();
    LogEvents.Transcript.BeginDurationMeasure(DurationMeasureType.Normal, TranscriptKeys.DurationInPane);
    InitializeTranscriptionData(this.props.history);
  }

  public componentWillUnmount() {
    LogEvents.Transcript.End();
    this._isMounted = false;
  }

  public componentDidUpdate() {
    LogEvents.RenderToPane.End([
      { key: RenderToPaneKeys.RenderedSuccessfully, value: true },
      { key: RenderToPaneKeys.IsShowingAudioFile, value: this.state.sourceFile != null },
      { key: RenderToPaneKeys.IsTranscriptEmpty, value: this.state.transcriptionData.length === 0 },
    ]);
  }

  private getAudioPlayerElement(sourceFile: ITranscriptionSourceFile) {
    const transcribingStyles = getTranscribingPageStyles(sourceFile != null);

    if (sourceFile == null) {
      return (
        <div className={transcribingStyles.noAudioFileContainer}>
          <AppExplainedLabel label={TranscriptionStrings.UnableToOpenAudioFileLabel} labelStyles={getExplainedLabelStyles()} iconName="Error" explanation={TranscriptionStrings.UnableToOpenAudioFileExplanation} />
        </div>
      );
    }

    return (
      <div className={transcribingStyles.playerContainer}>
        <AppAudioPlayer
          type={'AppAudioPlayerProps'}
          audioSrc={sourceFile.audioUrl}
          audioPlayerTitle={sourceFile.fileName}
          audioPlayerTitleLink={sourceFile.permanentAudioUrl}
          componentRef={this.audioPlayerRef}
          onAudioControlLoaded={this._onAudioControlLoaded}
          onAudioPositionUpdated={this._setHighlightedCard}
          onUserInteraction={this._onAudioPlayerUserInteraction} />
      </div>
    );
  }

  public render() {
    const { sourceFile, editingIndex, highlightedIndex, transcriptionData } = this.state;
    const transcribingStyles = getTranscribingPageStyles(this.state.sourceFile != null);

    return (
      <Stack>
        {this.getAudioPlayerElement(sourceFile)}
        <div className={transcribingStyles.cardsContainer} data-testid="transcript-items">
          {
            transcriptionData.map((transcriptionData, index) => {
              return <AppTranscriptCard
                key={index}
                type={'AppTranscriptCardProps'}
                transcriptionData={transcriptionData}
                onTimestampClicked={sourceFile != null ? this._onTimestampClicked : null}
                onTranscriptChange={this._onTranscriptionDataChanged}
                isEditing={editingIndex === index}
                onEditing={this._onEditingClicked}
                onEditingModeExit={this._onEditingModeExit}
                isHighlighted={index === highlightedIndex}
                shouldScrollToHighlightedIndex={this.shouldScrollToHighlightedIndex()}
                addTranscriptionSegmentToDocument={this._onAddTranscriptionSegmentToDocument} />
            })
          }
        </div>
        <div className={transcribingStyles.buttonContainer}>
          <Stack horizontal horizontalAlign="center" tokens={getButtonContainerStackTokens()}>
            <AppButton
              type={'AppButtonProps'}
              styles={getAddToDocumentButtonCustomStyles()}
              id={'addToDocument'}
              text={TranscriptionStrings.AddTranscriptionToDocumentButtonLabel}
              onExecute={this._onAddTranscriptionToDocumentClicked} />
            <AppButton
              type={'AppButtonProps'}
              styles={getStartOverButtonCustomStyles()}
              id={'startOver'}
              text={TranscriptionStrings.StartNewTranscriptionButtonLabel}
              onExecute={this._onStartOverClicked} />
          </Stack>
        </div>
        {
          this.state.showStartOverConfirmDialog ? this._showConfirmNewTranscriptDialog(): null
        }
      </Stack>
    );
  }

  private _showConfirmNewTranscriptDialog() {
    // VSO:4379118 tracks removing hard-coded string below
    // and in the OK confirmation on dialog below
    const confirmNewTranscriptDialogTitle: string | JSX.Element = 
        <Label styles={getTopLabelStyles()}>
          {TranscriptionStrings.ConfirmNewTranscriptionDialogTitle}
        </Label>

    return <Dialog
        dialogContentProps={getDialogContentProps(confirmNewTranscriptDialogTitle)}
        modalProps={getModalProps()}
        hidden={!this.state.showStartOverConfirmDialog}>
        <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{TranscriptionStrings.ConfirmNewTranscriptionDialogMessage}</Label>
        <DialogFooter>
            <AppButton 
                type={'AppButtonProps'} 
                id="OkNewTranscriptConfirmed" 
                styles={getSecondaryButtonOverrideStyles()}
                text={TranscriptionStrings.OKLabel}
                onExecute={this._onStartOverConfirmed} 
            />
            <AppButton 
                type={'AppButtonProps'} 
                id="NewTranscriptCanceled" 
                text={TranscriptionStrings.CancelButtonName}
                onExecute={this._onStartOverCanceled}
            />
        </DialogFooter>
    </Dialog>;
  }

  private shouldScrollToHighlightedIndex() {
    // return true if audio is playing and no card is being edited (editingIndex == -1)
    return this.state.isAudioPlaying && this.state.editingIndex == INDEX_NONE;
  }
  private _onStoreChanged = (): void => {
    if(this._isMounted) {
      this.setState(transcribingStore.getState());
    }
  };

  private _onAddTranscriptionToDocumentClicked = async () => {
    LogEvents.Transcript.IncrementCount(TranscriptKeys.TranscriptInsertedFull);
    await AddTranscriptionToDocument(this.state.transcriptionManager, this.props.history, this.state.sourceFile);
  };

  private _onAddTranscriptionSegmentToDocument = (id: number): void => {
    LogEvents.Transcript.IncrementCount(TranscriptKeys.TranscriptInsertedSection);
    AddTranscriptionSegmentToDocument(this.state.transcriptionManager, id, this.props.history);
  };

  private _onTimestampClicked = (time: number): void => {
    LogEvents.Transcript.IncrementCount(TranscriptKeys.TranscriptTimestampClicks);
    if (!this.audioPlayerRef.current) {
      return;
    }

    LogEvents.Transcript.BeginDurationMeasure(DurationMeasureType.Accumulated, TranscriptKeys.PlayDurationAcrossSessionSecs);

    this.audioPlayerRef.current.seekTime(time);
    SetAudioPlaying(true);
  };

  private _onTranscriptionDataChanged = async (data: AppTranscriptCardChangeParameter) => {
    LogEvents.Transcript.IncrementCount(TranscriptKeys.TranscriptEditSaved);
    LogEvents.Transcript.BeginDurationMeasure(DurationMeasureType.Slowest, TranscriptKeys.TranscriptEditSaveDurationSlowest)
    await TranscriptionDataModified(this.state.transcriptionManager, data, this.props.history);
    LogEvents.Transcript.EndDurationMeasure(TranscriptKeys.TranscriptEditSaveDurationSlowest);
  }

  private _onEditingClicked = (index: number) => {
    LogEvents.Transcript.IncrementCount(TranscriptKeys.TranscriptEditStarted);
    SetEditingMode(index);
  };

  private _onEditingModeExit = () => {
    LogEvents.Transcript.IncrementCount(TranscriptKeys.TranscriptEditClosed);
    SetEditingMode();
  };

  private _setHighlightedCard = (audioPosition: number): void => {
    let highlightIndex = -1;
    this.state.transcriptionData.map((transcriptionData, index) => {
      if (transcriptionData.timestamp > audioPosition) {
        return;
      }

      highlightIndex = index;
    });

    SetHighlightedIndex(highlightIndex);
  };

  private _onStartOverClicked = () => {
    LogEvents.StartOver.Start([{ key: StartOverKeys.Reason, value: "User" }]);
    SetShowStartOverConfirmDialog(true);
  };

  private _onStartOverConfirmed = async () => {
    SetShowStartOverConfirmDialog(false);
    await StartOverClicked(this.props.history);
    LogEvents.StartOver.End([{key: StartOverKeys.UserResult, value: "confirmed"}]);
  }

  private _onStartOverCanceled = () => {
    SetShowStartOverConfirmDialog(false);
    LogEvents.StartOver.End([{key: StartOverKeys.UserResult, value: "canceled"}]);
  }

  private _onAudioPlayerUserInteraction = (feature: AppAudioPlayerFeatureName): void => {
    LogEvents.Transcript.IncrementCount(this.TranscriptKeyFromAppAudioPlayerFeatureName(feature));
    if (feature == AppAudioPlayerFeatureName.PlayAudio) {
      LogEvents.Transcript.BeginDurationMeasure(DurationMeasureType.Accumulated, TranscriptKeys.PlayDurationAcrossSessionSecs);
      SetAudioPlaying(true);
    } else if (feature == AppAudioPlayerFeatureName.PauseAudio) {
      LogEvents.Transcript.EndDurationMeasure(TranscriptKeys.PlayDurationAcrossSessionSecs);
      SetAudioPlaying(false);
    }
  };

  private _onAudioControlLoaded = (duration: number): void => {
    LogEvents.Transcript.AddData([{ key: TranscriptKeys.AudioLengthSeconds, value: duration }]);
  }

  private TranscriptKeyFromAppAudioPlayerFeatureName(feature: AppAudioPlayerFeatureName): TranscriptKeys {
    switch (feature) {
      case AppAudioPlayerFeatureName.PauseAudio:
        return TranscriptKeys.PauseAudio;
      case AppAudioPlayerFeatureName.PlayAudio:
        return TranscriptKeys.PlayAudio;
      case AppAudioPlayerFeatureName.PlaybackSpeedChange:
        return TranscriptKeys.PlaybackSpeedChange;
      case AppAudioPlayerFeatureName.SeekBackward:
        return TranscriptKeys.SeekBackward;
      case AppAudioPlayerFeatureName.SeekForward:
        return TranscriptKeys.SeekForward;
      case AppAudioPlayerFeatureName.ShowVolume:
        return TranscriptKeys.ShowVolume;
      case AppAudioPlayerFeatureName.VolumeChange:
        return TranscriptKeys.VolumeChange;
      default:
        return TranscriptKeys.UnknownAudioFeature;
    }
  }
}
